<script setup lang="ts">
/**
 * GPickerWheel - iOS-style scroll picker wheel
 *
 * A touch-friendly scroll picker that snaps to items, similar to native iOS date pickers.
 * Used as a building block for GSheetDatepicker and similar components.
 */
import { nextTick, onMounted, ref, watch } from 'vue'

const ITEM_HEIGHT = 44 // Height of each item in pixels

withDefaults(defineProps<{
  modelValue?: any
  options: any[]
  labelProp?: string
  valueProp?: string
}>(), { labelProp: 'label',valueProp: 'value', })

const emit = defineEmits<{
  'update:modelValue': [value: any]
}>()

const containerRef = ref<HTMLElement>()
const isScrolling = ref(false)
let scrollTimeout: ReturnType<typeof setTimeout> | null = null

// Find the index of the currently selected value
function getSelectedIndex (): number {
  if (__props.modelValue === undefined || __props.modelValue === null) return 0
  const index = __props.options.findIndex(opt => opt[__props.valueProp] === __props.modelValue)
  return index >= 0 ? index : 0
}

// Scroll to a specific index
function scrollToIndex (index: number, smooth = true) {
  if (!containerRef.value) return
  const targetScroll = index * ITEM_HEIGHT
  containerRef.value.scrollTo({
    top: targetScroll,
    behavior: smooth ? 'smooth' : 'instant',
  })
}

// Handle scroll end - snap to nearest item
function handleScrollEnd () {
  if (!containerRef.value) return

  const scrollTop = containerRef.value.scrollTop
  const nearestIndex = Math.round(scrollTop / ITEM_HEIGHT)
  const clampedIndex = Math.max(0, Math.min(nearestIndex, __props.options.length - 1))

  // Snap to nearest item
  scrollToIndex(clampedIndex)

  // Emit the selected value
  const selectedOption = __props.options[clampedIndex]
  if (selectedOption && selectedOption[__props.valueProp] !== __props.modelValue) {
    emit('update:modelValue', selectedOption[__props.valueProp])
  }

  isScrolling.value = false
}

// Handle scroll event with debounce
function onScroll () {
  isScrolling.value = true
  if (scrollTimeout) clearTimeout(scrollTimeout)
  scrollTimeout = setTimeout(handleScrollEnd, 100)
}

// Handle click on an item
function onItemClick (index: number) {
  scrollToIndex(index)
  const selectedOption = __props.options[index]
  if (selectedOption) {
    emit('update:modelValue', selectedOption[__props.valueProp])
  }
}

// Initialize scroll position
onMounted(() => {
  nextTick(() => {
    const index = getSelectedIndex()
    scrollToIndex(index, false)
  })
})

// Watch for external value changes
watch(() => __props.modelValue, () => {
  if (!isScrolling.value) {
    const index = getSelectedIndex()
    scrollToIndex(index)
  }
})

// Watch for options changes
watch(() => __props.options, () => {
  nextTick(() => {
    const index = getSelectedIndex()
    scrollToIndex(index, false)
  })
}, { deep: true })
</script>

<template>
  <div class="g-picker-wheel">
    <!-- Selection highlight bar -->
    <div class="g-picker-wheel-highlight"/>

    <!-- Scrollable container -->
    <div
      ref="containerRef"
      class="g-picker-wheel-scroll"
      @scroll="onScroll"
    >
      <!-- Top padding (2 items worth) -->
      <div class="g-picker-wheel-padding"/>

      <!-- Items -->
      <div
        v-for="(option, index) in options"
        :key="option[valueProp]"
        class="g-picker-wheel-item"
        :class="{ selected: option[valueProp] === modelValue }"
        @click="onItemClick(index)"
      >
        {{ option[labelProp] }}
      </div>

      <!-- Bottom padding (2 items worth) -->
      <div class="g-picker-wheel-padding"/>
    </div>

    <!-- Fade overlays -->
    <div class="g-picker-wheel-fade-top"/>
    <div class="g-picker-wheel-fade-bottom"/>
  </div>
</template>

<style lang="scss">
.g-picker-wheel {
  @apply relative flex-1;

  height: 220px; // 5 visible items * 44px
  overflow: hidden;
}

.g-picker-wheel-scroll {
  @apply h-full overflow-y-auto;

  -webkit-overflow-scrolling: touch;
  -ms-overflow-style: none;
  scroll-snap-type: y mandatory;

  // Hide scrollbar
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.g-picker-wheel-padding {
  flex-shrink: 0;
  height: 88px; // 2 items worth of padding
}

.g-picker-wheel-item {
  @apply flex items-center justify-center;
  @apply text-lg font-medium text-v2-secondary;
  @apply cursor-pointer select-none;

  height: 44px;
  scroll-snap-align: center;
  transition: color 0.15s, opacity 0.15s;

  &.selected {
    @apply text-navy font-semibold;
  }
}

.g-picker-wheel-highlight {
  @apply absolute left-0 right-0 pointer-events-none;
  @apply border-t border-b border-grey-primary;

  height: 44px;
  top: 88px; // 2 items from top
}

.g-picker-wheel-fade-top,
.g-picker-wheel-fade-bottom {
  @apply absolute left-0 right-0 pointer-events-none;

  height: 88px;
}

.g-picker-wheel-fade-top {
  @apply top-0;

  background: linear-gradient(to bottom, rgb(255 255 255 / 90%), rgb(255 255 255 / 0%));
}

.g-picker-wheel-fade-bottom {
  @apply bottom-0;

  background: linear-gradient(to top, rgb(255 255 255 / 90%), rgb(255 255 255 / 0%));
}
</style>
